import { ChatMessage } from '../types/chat';

export type ChatServiceResponse = {
  message: string;
  redirectToHubSpot?: boolean;
  hubSpotMessage?: string;
  retryAfter?: number;
  requiresVerification?: boolean;
  challenge?: string;
  challengeAnswer?: string;
  showLeadForm?: boolean;
  leadFormReason?: string;
  leadSubmitted?: boolean;
  replyLanguage?: string;
};

export type StreamHandlers = {
  onToken?: (token: string) => void;
  onReplace?: (message: string) => void;
  onLeadForm?: (payload: { reason?: string; language?: string }) => void;
  onDone?: (payload: ChatServiceResponse) => void;
  onError?: (error: Error) => void;
};

export class NetlifyChatService {
  private static instance: NetlifyChatService;
  private conversationHistory: ChatMessage[] = [];

  public static getInstance(): NetlifyChatService {
    if (!NetlifyChatService.instance) {
      NetlifyChatService.instance = new NetlifyChatService();
    }
    return NetlifyChatService.instance;
  }

  private getFunctionUrl(): string {
    if (typeof window !== 'undefined') {
      const currentProtocol = window.location.protocol;
      const currentHostname = window.location.hostname;
      const netlifyDevPort = '8888';

      if (currentHostname === 'localhost' || currentHostname === '127.0.0.1') {
        return `${currentProtocol}//${currentHostname}:${netlifyDevPort}/.netlify/functions/chat`;
      }
    }

    return '/.netlify/functions/chat';
  }

  private pushUserMessage(userMessage: string) {
    this.conversationHistory.push({
      id: Date.now().toString(),
      content: userMessage,
      role: 'user',
      timestamp: new Date(),
    });
  }

  private pushAssistantMessage(assistantMessage: string) {
    this.conversationHistory.push({
      id: (Date.now() + 1).toString(),
      content: assistantMessage,
      role: 'assistant',
      timestamp: new Date(),
    });

    if (this.conversationHistory.length > 10) {
      this.conversationHistory = this.conversationHistory.slice(-10);
    }
  }

  private getHistoryPayload() {
    return this.conversationHistory.slice(-10).map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));
  }

  private handleErrorPayload(data: any, status: number): ChatServiceResponse {
    if (data?.redirectToHubSpot) {
      return {
        message:
          data.hubSpotMessage ||
          'Please use our HubSpot chat for immediate assistance.',
        redirectToHubSpot: true,
        hubSpotMessage: data.hubSpotMessage,
        retryAfter: data.retryAfter,
      };
    }

    if (data?.errorCode === 'bot_detected' || data?.errorCode === 'suspicious_behavior') {
      throw new Error('Access denied. Bot activity detected.');
    }

    if (data?.errorCode === 'human_verification_required') {
      return {
        message: data.error,
        challenge: data.challenge,
        challengeAnswer: data.challengeAnswer,
        requiresVerification: true,
      };
    }

    throw new Error(data?.error || `HTTP error! status: ${status}`);
  }

  public async sendMessage(
    userMessage: string,
    options: { preferredLanguage?: string } = {},
  ): Promise<ChatServiceResponse> {
    try {
      this.pushUserMessage(userMessage);
      const conversationHistory = this.getHistoryPayload();
      const functionUrl = this.getFunctionUrl();

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          conversationHistory,
          preferredLanguage: options.preferredLanguage,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return this.handleErrorPayload(data, response.status);
      }

      if (data.error) {
        throw new Error(data.error);
      }

      const assistantMessage = data.message;
      this.pushAssistantMessage(assistantMessage);
      return { message: assistantMessage };
    } catch (error) {
      console.error('Error calling Netlify Function:', error);
      if (error instanceof SyntaxError) {
        throw new Error(
          'Unable to reach the chat service. Please refresh the page and try again.',
        );
      }
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to get response from AI assistant. Please try again later.');
    }
  }

  /**
   * Stream an assistant reply over SSE. Falls back to sendMessage if the
   * response is not an event stream (e.g. HubSpot redirect JSON).
   */
  public async streamMessage(
    userMessage: string,
    handlers: StreamHandlers = {},
    options: { preferredLanguage?: string } = {},
  ): Promise<ChatServiceResponse> {
    this.pushUserMessage(userMessage);
    const conversationHistory = this.getHistoryPayload();
    const functionUrl = this.getFunctionUrl();

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({
        message: userMessage,
        conversationHistory,
        stream: true,
        preferredLanguage: options.preferredLanguage,
      }),
    });

    const contentType = response.headers.get('content-type') || '';

    // Non-stream JSON control responses (rate limit, challenge, errors)
    if (!contentType.includes('text/event-stream')) {
      const data = await response.json();
      if (!response.ok) {
        // Roll back the optimistic user history entry for failed control paths
        // so a retry doesn't duplicate — keep user message since UI already shows it.
        return this.handleErrorPayload(data, response.status);
      }
      if (data.error) {
        throw new Error(data.error);
      }
      this.pushAssistantMessage(data.message);
      handlers.onDone?.({ message: data.message });
      return { message: data.message };
    }

    if (!response.ok || !response.body) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let assembled = '';
    let finalPayload: ChatServiceResponse | null = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';

      for (const part of parts) {
        const line = part
          .split('\n')
          .map((l) => l.trim())
          .find((l) => l.startsWith('data:'));
        if (!line) continue;

        let event: any;
        try {
          event = JSON.parse(line.slice(5).trim());
        } catch {
          continue;
        }

        if (event.type === 'token' && typeof event.token === 'string') {
          assembled += event.token;
          handlers.onToken?.(event.token);
        } else if (event.type === 'replace' && typeof event.message === 'string') {
          assembled = event.message;
          handlers.onReplace?.(event.message);
        } else if (event.type === 'lead_form') {
          handlers.onLeadForm?.({ reason: event.reason, language: event.language });
        } else if (event.type === 'done') {
          assembled = event.message || assembled;
          finalPayload = {
            message: assembled,
            showLeadForm: Boolean(event.showLeadForm),
            leadFormReason: event.leadFormReason,
            leadSubmitted: Boolean(event.leadSubmitted),
            replyLanguage: event.replyLanguage,
          };
          if (finalPayload.showLeadForm) {
            handlers.onLeadForm?.({
              reason: finalPayload.leadFormReason,
              language: finalPayload.replyLanguage,
            });
          }
          handlers.onDone?.(finalPayload);
        } else if (event.type === 'error') {
          const err = new Error(event.error || 'Stream error');
          handlers.onError?.(err);
          throw err;
        }
      }
    }

    if (!finalPayload) {
      finalPayload = { message: assembled };
      handlers.onDone?.(finalPayload);
    }

    this.pushAssistantMessage(finalPayload.message);
    return finalPayload;
  }

  public clearHistory(): void {
    this.conversationHistory = [];
  }

  /** Append an assistant note that was generated outside the LLM stream (e.g. lead form). */
  public recordAssistantNote(content: string): void {
    this.pushAssistantMessage(content);
  }
}

export default NetlifyChatService.getInstance();
